import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * `prefers-reduced-motion`. Lives beside the reveal because it is the reveal's own
 * switch: a reveal is motion, and motion that gates reading is an accessibility
 * fault rather than a flourish — so the setting is honoured by showing text WHOLE,
 * never by animating it faster.
 *
 * Shared rather than redefined per surface (it was local to OpeningRun until the
 * thread needed it too, 2026-08-22). Two copies of a media-query hook drift.
 */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
  )
  useEffect(() => {
    if (typeof matchMedia !== 'function') return
    const q = matchMedia('(prefers-reduced-motion: reduce)')
    const on = () => setReduced(q.matches)
    q.addEventListener('change', on)
    return () => q.removeEventListener('change', on)
  }, [])
  return reduced
}

/**
 * Reveals text word-by-word over `revealMs`.
 *
 * Shared by every surface that renders arriving speech. The duration is the
 * engine's, not the client's: spoken words genuinely take that long to say
 * (doctrine-spoken-word-has-duration), and on a channel the decode IS the
 * medium. Zero means the text arrives whole.
 *
 * Runs once per mount — a re-render must never restart a reveal, or the column
 * stutters as siblings arrive.
 */
export function useReveal(text: string, revealMs: number): string {
  const words = useMemo(() => text.split(' '), [text])
  const [shown, setShown] = useState(() => (revealMs > 0 ? 0 : words.length))
  const started = useRef(false)

  useEffect(() => {
    if (revealMs <= 0 || started.current) return
    started.current = true

    const step = Math.max(24, revealMs / Math.max(1, words.length))
    let i = 0
    const id = window.setInterval(() => {
      i += 1
      setShown(i)
      if (i >= words.length) window.clearInterval(id)
    }, step)
    return () => {
      window.clearInterval(id)
      // ⚠ THE GUARD MUST BE RELEASED HERE, or StrictMode kills every reveal (2026-08-21).
      //
      // React 18 StrictMode double-invokes effects in dev: run → cleanup → run. The first pass
      // set the ref and started an interval; the cleanup cleared that interval; the second pass
      // saw the ref and returned early. Net result, no interval at all — reveals sat frozen at
      // zero words in dev and worked in production, which is the worst shape a bug can take.
      //
      // The ref's stated job — "a re-render must never restart a reveal" — is already done by
      // the empty dependency array, which is why releasing it here costs nothing.
      started.current = false
    }
    // Mount-only by design.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return revealMs > 0 ? words.slice(0, shown).join(' ') : text
}
